import { useState, useEffect } from 'react'
import { backendGetRequest } from '../../../helpers/callBackend'
import { errorMessage } from '../../../helpers/errorMessageFunc'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import OutlinedInput from '@mui/material/OutlinedInput'
import ClearIcon from '@mui/icons-material/Clear'
import theme from '../../../helpers/theme'
import ExpandMoreSharpIcon from '@mui/icons-material/ExpandMoreSharp'
import Typography from '@mui/material/Typography'

const EntitySelect = ({ onChange, disabled, onClear, value, autoLoad = false, placeholder = '' }) => {
  const [entities, setEntities] = useState([])
  const [totalRecords, setTotalRecords] = useState(-1)
  const [isLoading, setIsLoading] = useState(false)
  const [items, setItems] = useState([])
  const [currentValue, setCurrentValue] = useState(value || '')
  const [entitiesMap, setEntitiesMap] = useState({})

  const placeholderText = placeholder || 'Select a holding entity'

  const getEntitesPage = async (pageNum) => {
    try {
      const entityResp = await backendGetRequest('/api/entity', {
        params: {
          page_index: pageNum,
        },
      })
      const entitiesArray = entityResp.data?.entities
      setEntities((prevState) => [...prevState, ...entitiesArray])
    } catch (error) {
      errorMessage('Error fetching entities')
    }
  }

  const getEntities = async () => {
    setIsLoading(true)
    let totalPages = 0
    try {
      const pageInfo = await backendGetRequest('/api/entity', {
        params: {
          page_index: 0,
        },
      })
      totalPages = pageInfo.data?.total_pages
      const totalRecs = pageInfo.data?.total_records
      if (totalRecs === 0 && totalRecords < 0) {
        setTotalRecords(0)
      } else {
        setTotalRecords((prevState) => prevState + pageInfo.data?.total_records)
      }
    } catch {
      return
    }

    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
      getEntitesPage(pageNum)
    }
  }

  const handleClear = () => {
    setCurrentValue('')
    onClear()
  }

  const handleChange = (event) => {
    setCurrentValue(event.target.value)
    onChange(event)
  }

  useEffect(() => {
    if (totalRecords >= 0 && entities.length >= totalRecords) {
      setIsLoading(false)
    }
    if (entities.length > 0) {
      const newItems = entities.map((entity) => {
        return (
          <MenuItem value={entity.id} key={entity.entity_info.name}>
            {entity.entity_info.name}
          </MenuItem>
        )
      })
      setItems(newItems)

      let newMap = {}
      entities.forEach((entity) => {
        newMap[entity.id] = entity.entity_info.name
      })
      setEntitiesMap(newMap)
    }
  }, [entities])

  useEffect(() => {
    if (autoLoad && !entities.length) {
      getEntities()
    }
  }, [autoLoad])

  useEffect(() => {
    setCurrentValue(value || '')
  }, [value])

  return (
    <FormControl fullWidth variant='outlined' size='small'>
      <Select
        label={placeholderText}
        value={currentValue}
        onChange={handleChange}
        disabled={isLoading || disabled}
        IconComponent={ExpandMoreSharpIcon}
        sx={{
          borderRadius: 0,
          '&.Mui-disabled': {
            backgroundColor: theme.palette.secondary[100],
          },
        }}
        displayEmpty
        renderValue={(selected) => {
          if (!selected) {
            return <Typography color='secondary'>{placeholderText}</Typography>
          }
          return entitiesMap[selected]
        }}
        input={
          <OutlinedInput
            endAdornment={
              currentValue && (
                <InputAdornment position='end'>
                  <IconButton onClick={handleClear}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }
          />
        }
      >
        {items}
      </Select>
    </FormControl>
  )
}

export default EntitySelect
